import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Company } from 'src/api/types';

type SelectedClient = {
  id: number;
  name: string;
  email: string;
  state: string;
  avatarUrl: string;
  status: "approved";
  link_id?: number;
  companies?: Company[];
} | null;

interface SelectedClientState {
  client: SelectedClient;
  loadingCompanies: boolean;
  companiesError: string | null;
}

const getInitialClient = (): SelectedClient => {
  try {
    const savedClient = localStorage.getItem('selectedClient');
    if (!savedClient) return null;

    const client = JSON.parse(savedClient);
    if (client?.status !== "approved") {
      localStorage.removeItem('selectedClient');
      return null;
    }
    return client;
  } catch (error) {
    console.error('Error parsing saved client:', error);
    return null;
  }
};

const initialState: SelectedClientState = {
  client: getInitialClient(),
  loadingCompanies: false,
  companiesError: null,
};

export const setClientCompanies = createAsyncThunk(
  'selectedClient/setClientCompanies',
  async ({ clientId, companies }: { clientId: number; companies: Company[] }) => {
    return { clientId, companies };
  }
);

const selectedClientSlice = createSlice({
  name: "selectedClient",
  initialState,
  reducers: {
    setSelectedClient: (state, action: PayloadAction<SelectedClient>) => {
      const client = action.payload?.status === "approved" ? action.payload : null;
      state.client = client;
      
      // Сохраняем в localStorage
      if (client) {
        localStorage.setItem('selectedClient', JSON.stringify(client));
      } else {
        localStorage.removeItem('selectedClient');
      }
    
    },
    clearSelectedClient: (state) => {
      state.client = null;
      state.loadingCompanies = false;
      state.companiesError = null;
      
      localStorage.removeItem('selectedClient');
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(setClientCompanies.pending, (state) => {
        state.loadingCompanies = true;
        state.companiesError = null;
      })
      .addCase(setClientCompanies.fulfilled, (state, action) => {
        state.loadingCompanies = false;
        if (state.client && state.client.id === action.payload.clientId) {
          state.client.companies = action.payload.companies;
          
          localStorage.setItem('selectedClient', JSON.stringify(state.client));
        }
      })
      .addCase(setClientCompanies.rejected, (state, action) => {
        state.loadingCompanies = false;
        state.companiesError = action.error.message || 'Failed to load companies';
      });
  },
});

export const { setSelectedClient, clearSelectedClient } = selectedClientSlice.actions;
export default selectedClientSlice.reducer;
